import {
  FISH_PRICE_WON_VALUES,
  FISH_PRODUCT_IDS,
  type FishPricesByProduct,
  type FishPriceWon,
  type FishProductId,
  type FishShopV2Phase,
  type FishShopV2ViewerState,
  type FishTicketsByProduct,
} from "@cloudnest/fish-shop-v2-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import {
  fishShopMultiplayerActions,
  useFishShopMultiplayerStore,
} from "./multiplayer-game-store";
import "./styles/fish-shop-tokens.css";

interface ProductMeta {
  readonly productId: FishProductId;
  readonly label: string;
  readonly className: string;
}

type TicketDraft = Record<FishProductId, number>;
type PriceDraft = Partial<Record<FishProductId, FishPriceWon>>;
type FishShopV2ViewerPlayer = FishShopV2ViewerState["players"][number];
type GuidanceTone =
  | "action"
  | "waiting"
  | "private"
  | "observer"
  | "danger"
  | "complete";

interface ActionGuidance {
  readonly title: string;
  readonly detail: string;
  readonly meta: string;
  readonly tone: GuidanceTone;
}

const PRODUCTS: readonly ProductMeta[] = [
  {
    productId: "HAIRTAIL",
    label: "갈치",
    className: "fishv2-icon-hairtail fishv2-product-hairtail",
  },
  {
    productId: "MACKEREL",
    label: "고등어",
    className: "fishv2-icon-mackerel fishv2-product-mackerel",
  },
  {
    productId: "SQUID",
    label: "오징어",
    className: "fishv2-icon-squid fishv2-product-squid",
  },
] as const;

const PHASE_STEPS: readonly {
  readonly phase: FishShopV2Phase;
  readonly label: string;
  readonly help: string;
}[] = [
  { phase: "TICKET_ALLOCATION", label: "판매권 배분", help: "비공개 제출" },
  { phase: "MARKET_REVEALED", label: "시장 공개", help: "판매자 확인" },
  { phase: "PRICE_SUBMISSION", label: "가격 제출", help: "딜러룸" },
  { phase: "ROUND_REVEALED", label: "결과 공개", help: "확인 대기" },
  { phase: "RESTART_READY", label: "재시작", help: "전원 동점" },
  { phase: "COMPLETED", label: "종료", help: "최종 공개" },
] as const;

function createTicketDraft(): TicketDraft {
  return { HAIRTAIL: 0, MACKEREL: 0, SQUID: 0 };
}

function formatWon(value: number): string {
  return `${new Intl.NumberFormat("ko-KR").format(value)}원`;
}

function getProductMeta(productId: FishProductId): ProductMeta {
  return PRODUCTS.find((product) => product.productId === productId)!;
}

function sumTickets(tickets: Readonly<Record<FishProductId, number>>): number {
  return FISH_PRODUCT_IDS.reduce(
    (total, productId) => total + tickets[productId],
    0,
  );
}

function buildPrices(
  productIds: readonly FishProductId[],
  draft: PriceDraft,
): FishPricesByProduct {
  return Object.fromEntries(
    productIds.map((productId) => [productId, draft[productId] ?? 1000]),
  ) as FishPricesByProduct;
}

function getItemStatusLabel(
  status: FishShopV2ViewerPlayer["itemStatus"],
): string {
  if (status === "AVAILABLE") return "아이템 사용 가능";
  if (status === "USED_CHECK") return "확인 사용 완료";
  if (status === "USED_SECRET") return "비밀 사용 완료";
  return "아이템 비공개";
}

function getMarketProductSummary(
  state: FishShopV2ViewerState,
  productId: FishProductId,
): string {
  const market = state.currentRound.market[productId];
  return `총 ${market.soldTicketCount}장 · 판매자 ${market.sellers.length}명`;
}

function getEligibleProductIds(
  state: FishShopV2ViewerState,
  playerId: string | null,
): readonly FishProductId[] {
  if (!playerId) return [];
  return FISH_PRODUCT_IDS.filter((productId) =>
    state.currentRound.market[productId].sellers.some(
      (seller) => seller.playerId === playerId,
    ),
  );
}

function getActionGuidance({
  state,
  readOnlyObserver,
  canAllocate,
  canSubmitPrice,
  eligibleProductIds,
  ownPrice,
  needsResultConfirm,
  isHost,
  draftTotal,
  remainingTickets,
}: {
  readonly state: FishShopV2ViewerState;
  readonly readOnlyObserver: boolean;
  readonly canAllocate: boolean;
  readonly canSubmitPrice: boolean;
  readonly eligibleProductIds: readonly FishProductId[];
  readonly ownPrice: FishPricesByProduct | null;
  readonly needsResultConfirm: boolean;
  readonly isHost: boolean;
  readonly draftTotal: number;
  readonly remainingTickets: number | null;
}): ActionGuidance {
  const allocatedCount = state.currentRound.allocatedPlayerIds.length;
  const allocationPendingCount = state.players.length - allocatedCount;
  const submittedPriceCount = state.currentRound.priceSubmittedPlayerIds.length;
  const priceSubmitterCount = state.currentRound.priceSubmitterIds.length;
  const pricePendingCount = priceSubmitterCount - submittedPriceCount;
  const confirmPendingCount =
    state.players.length - state.currentRound.confirmedPlayerIds.length;

  if (readOnlyObserver) {
    return {
      title: "관전 모드",
      detail: "공개된 판매 시장, 제출 현황, 라운드 결과만 표시됩니다.",
      meta: "공개 정보만",
      tone: "observer",
    };
  }

  if (state.currentRound.phase === "TICKET_ALLOCATION") {
    if (canAllocate) {
      return {
        title: "내 행동 필요 · 판매권 배분",
        detail: `남은 ${remainingTickets ?? "-"}장 중 ${draftTotal}장을 이번 라운드에 사용합니다. 0장 패스도 가능합니다.`,
        meta: `${allocatedCount}/${state.players.length} 배분 완료`,
        tone: "action",
      };
    }
    return {
      title: "배분 완료 · 시장 공개 대기",
      detail: `${allocationPendingCount}명이 아직 배분 중입니다. 내 배분 수량은 시장 공개 전까지 비공개입니다.`,
      meta: `${allocatedCount}/${state.players.length} 배분 완료`,
      tone: "waiting",
    };
  }

  if (
    state.currentRound.phase === "MARKET_REVEALED" ||
    state.currentRound.phase === "PRICE_SUBMISSION"
  ) {
    if (canSubmitPrice) {
      return {
        title: "내 행동 필요 · 희망가 제출",
        detail: `내가 판매한 ${eligibleProductIds.length}개 상품의 가격을 딜러룸에 제출하세요.`,
        meta: `${submittedPriceCount}/${priceSubmitterCount} 제출 완료`,
        tone: "action",
      };
    }
    if (ownPrice) {
      return {
        title: "희망가 제출 완료",
        detail:
          "결과 공개 전까지 내 희망가는 다른 플레이어에게 보이지 않습니다.",
        meta: `${submittedPriceCount}/${priceSubmitterCount} 제출 완료`,
        tone: "private",
      };
    }
    return {
      title: "가격 제출 없음 · 결과 대기",
      detail:
        "이번 라운드에 판매한 상품이 없어 희망가 제출 없이 결과 공개를 기다립니다.",
      meta: `${pricePendingCount}명 제출 대기`,
      tone: "waiting",
    };
  }

  if (state.currentRound.phase === "ROUND_REVEALED") {
    if (needsResultConfirm) {
      return {
        title: "내 행동 필요 · 결과 확인",
        detail: "상품별 최저가와 수입을 확인한 뒤 결과 확인 완료를 누르세요.",
        meta: `${confirmPendingCount}명 확인 필요`,
        tone: "action",
      };
    }
    return {
      title: "결과 확인 완료 · 다음 단계 대기",
      detail:
        "전원이 확인하면 다음 라운드, 재시작, 또는 최종 결과로 이동합니다.",
      meta: `${confirmPendingCount}명 확인 필요`,
      tone: "waiting",
    };
  }

  if (state.currentRound.phase === "RESTART_READY") {
    return {
      title: isHost ? "방장 행동 필요 · 처음부터 재시작" : "방장 재시작 대기",
      detail:
        "전원 동점입니다. 수입, 판매권, 아이템, 라운드가 모두 초기화됩니다.",
      meta: "전원 동점",
      tone: "danger",
    };
  }

  return {
    title: "최종 수입 공개",
    detail: "누적 수입, 승자, 탈락후보, 가넷 보상이 모두 공개되었습니다.",
    meta: "게임 종료",
    tone: "complete",
  };
}

function ProductLabel({
  productId,
  compact = false,
}: {
  readonly productId: FishProductId;
  readonly compact?: boolean;
}) {
  const meta = getProductMeta(productId);
  return (
    <span className="fishv2-product-label">
      <span
        className={`${compact ? "fishv2-ticket-icon" : "fishv2-product-icon"} ${meta.className}`}
        aria-hidden="true"
      />
      {meta.label}
    </span>
  );
}

function NextActionBanner({ guidance }: { readonly guidance: ActionGuidance }) {
  return (
    <div
      className={`fishv2-next-action ${guidance.tone}`}
      data-testid="fish-shop-v2-next-action"
    >
      <div>
        <strong>{guidance.title}</strong>
        <span>{guidance.detail}</span>
      </div>
      <span className="fishv2-pill">{guidance.meta}</span>
    </div>
  );
}

function PhaseRail({
  currentPhase,
}: {
  readonly currentPhase: FishShopV2Phase;
}) {
  const currentIndex = PHASE_STEPS.findIndex(
    (step) => step.phase === currentPhase,
  );

  return (
    <div className="fishv2-phase-rail" data-testid="fish-shop-v2-phase-rail">
      {PHASE_STEPS.map((step, index) => {
        const className =
          index < currentIndex
            ? "done"
            : step.phase === currentPhase
              ? "current"
              : "future";
        return (
          <div
            key={step.phase}
            className={`fishv2-phase-step ${className}`}
            data-testid={`fish-shop-v2-phase-${step.phase}`}
          >
            <strong>{step.label}</strong>
            <span>{step.help}</span>
          </div>
        );
      })}
    </div>
  );
}

function PlayerRail({
  state,
  myPlayerId,
  getPlayerName,
}: {
  readonly state: FishShopV2ViewerState;
  readonly myPlayerId: string | null;
  readonly getPlayerName: (playerId: string) => string;
}) {
  function getSeatStatus(playerId: string): {
    readonly className: string;
    readonly label: string;
  } {
    const isMe = playerId === myPlayerId;
    if (state.currentRound.phase === "TICKET_ALLOCATION") {
      const ready = state.currentRound.allocatedPlayerIds.includes(playerId);
      return {
        className: ready ? "ready" : "waiting",
        label: ready ? "배분 완료" : isMe ? "배분 필요" : "배분 대기",
      };
    }
    if (
      state.currentRound.phase === "MARKET_REVEALED" ||
      state.currentRound.phase === "PRICE_SUBMISSION"
    ) {
      if (!state.currentRound.priceSubmitterIds.includes(playerId)) {
        return { className: "idle", label: "제출 없음" };
      }
      const ready =
        state.currentRound.priceSubmittedPlayerIds.includes(playerId);
      return {
        className: ready ? "ready" : "waiting",
        label: ready ? "가격 완료" : isMe ? "가격 필요" : "가격 대기",
      };
    }
    if (state.currentRound.phase === "ROUND_REVEALED") {
      const ready = state.currentRound.confirmedPlayerIds.includes(playerId);
      return {
        className: ready ? "ready" : "waiting",
        label: ready ? "확인 완료" : isMe ? "확인 필요" : "확인 대기",
      };
    }
    if (state.currentRound.phase === "RESTART_READY") {
      return { className: "waiting", label: "재시작 대기" };
    }
    return { className: "ready", label: "최종 공개" };
  }

  return (
    <div className="fishv2-player-rail" data-testid="fish-shop-v2-player-rail">
      {state.players.map((player) => {
        const displayName =
          player.playerId === myPlayerId
            ? `나 · ${getPlayerName(player.playerId)}`
            : getPlayerName(player.playerId);
        const seatStatus = getSeatStatus(player.playerId);
        return (
          <div
            key={player.playerId}
            className={`fishv2-seat ${seatStatus.className} ${
              player.playerId === myPlayerId ? "current" : ""
            }`}
            data-testid="fish-shop-v2-player-seat"
            title={displayName}
          >
            <span className="fishv2-dot" />
            <span className="fishv2-seat-label">{displayName}</span>
            <span className="fishv2-seat-status">{seatStatus.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function PrivateInfoBlock({
  player,
}: {
  readonly player: FishShopV2ViewerPlayer | null;
}) {
  if (
    !player ||
    (player.itemStatus === "AVAILABLE" && player.checkSnapshotWon === null)
  ) {
    return null;
  }

  return (
    <div className="fishv2-private-box" data-testid="fish-shop-v2-private-info">
      <div className="fishv2-section-head compact">
        <div>
          <div className="fishv2-kicker">private info</div>
          <h3>나에게만 보임</h3>
        </div>
        <span className="fishv2-pill">
          {getItemStatusLabel(player.itemStatus)}
        </span>
      </div>
      {player.checkSnapshotWon !== null ? (
        <div
          className="fishv2-check-box fishv2-private-snapshot"
          data-testid="fish-shop-v2-check-snapshot"
        >
          <span>확인 결과</span>
          <strong>{formatWon(player.checkSnapshotWon)}</strong>
          <p className="fishv2-muted">
            현재 라운드까지의 내 누적 수입입니다. 다른 플레이어와 관전자에게는
            표시되지 않습니다.
          </p>
        </div>
      ) : (
        <p className="fishv2-muted">
          비밀 사용자는 공개되지 않으며, 선택한 상품의 결과만 라운드 공개 때
          숨겨집니다.
        </p>
      )}
    </div>
  );
}

function MarketGrid({
  state,
  myPlayerId,
  getPlayerName,
}: {
  readonly state: FishShopV2ViewerState;
  readonly myPlayerId: string | null;
  readonly getPlayerName: (playerId: string) => string;
}) {
  return (
    <section className="fishv2-card" data-testid="fish-shop-v2-market-grid">
      <div className="fishv2-section-head">
        <div>
          <div className="fishv2-kicker">public market</div>
          <h2>이번 라운드 판매 시장</h2>
        </div>
        <span className="fishv2-pill">판매자/수량만 공개</span>
      </div>
      <div className="fishv2-market">
        {PRODUCTS.map((product) => {
          const market = state.currentRound.market[product.productId];
          return (
            <div
              key={product.productId}
              className={`fishv2-product-lane ${product.className}`}
            >
              <div className="fishv2-product-head">
                <ProductLabel productId={product.productId} />
                <span className="fishv2-pill">
                  총 {market.soldTicketCount}장
                </span>
              </div>
              {market.sellers.length === 0 ? (
                <p className="fishv2-muted">이번 라운드 미판매</p>
              ) : (
                market.sellers.map((seller) => {
                  const isMine = seller.playerId === myPlayerId;
                  return (
                    <div key={seller.playerId} className="fishv2-seller-row">
                      <span className={isMine ? "fishv2-self-label" : ""}>
                        {isMine
                          ? `나 · ${getPlayerName(seller.playerId)}`
                          : getPlayerName(seller.playerId)}
                      </span>
                      <strong>{seller.ticketCount}장</strong>
                    </div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AllocationBoard({
  state,
  myPlayerId,
  draft,
  onChangeDraft,
  getPlayerName,
}: {
  readonly state: FishShopV2ViewerState;
  readonly myPlayerId: string | null;
  readonly draft: TicketDraft;
  readonly onChangeDraft: (draft: TicketDraft) => void;
  readonly getPlayerName: (playerId: string) => string;
}) {
  const me = state.players.find((player) => player.playerId === myPlayerId);
  const remainingTickets = me?.remainingTickets ?? 0;
  const draftTotal = sumTickets(draft);
  const remainingAfterDraft = remainingTickets - draftTotal;

  function updateProduct(productId: FishProductId, delta: number): void {
    const nextValue = Math.max(0, draft[productId] + delta);
    const nextDraft = { ...draft, [productId]: nextValue };
    if (sumTickets(nextDraft) <= remainingTickets) {
      onChangeDraft(nextDraft);
    }
  }

  return (
    <div className="fishv2-workspace">
      <section className="fishv2-card fishv2-action-focus">
        <div className="fishv2-section-head">
          <div>
            <div className="fishv2-kicker">ticket allocation</div>
            <h2>내 판매권 배분</h2>
          </div>
          <span className="fishv2-pill">남은 {remainingAfterDraft}장</span>
        </div>

        <div className="fishv2-grid fishv2-lane-grid">
          {PRODUCTS.map((product) => (
            <div
              key={product.productId}
              className={`fishv2-product-lane ${product.className}`}
            >
              <div className="fishv2-product-head">
                <ProductLabel productId={product.productId} />
                <span className="fishv2-pill">
                  {draft[product.productId]}장
                </span>
              </div>
              <div className="fishv2-ticket-controls">
                <button
                  type="button"
                  className="fishv2-icon-btn"
                  data-testid={`fish-shop-v2-ticket-${product.productId}-minus`}
                  onClick={() => updateProduct(product.productId, -1)}
                  disabled={draft[product.productId] <= 0}
                  aria-label={`${product.label} 판매권 줄이기`}
                >
                  -
                </button>
                <div className="fishv2-count">{draft[product.productId]}</div>
                <button
                  type="button"
                  className="fishv2-icon-btn"
                  data-testid={`fish-shop-v2-ticket-${product.productId}-plus`}
                  onClick={() => updateProduct(product.productId, 1)}
                  disabled={remainingAfterDraft <= 0}
                  aria-label={`${product.label} 판매권 늘리기`}
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <aside className="fishv2-card fishv2-side-panel">
        <h3>배분 진행</h3>
        <p className="fishv2-muted">
          내 수량은 시장 공개 전까지 다른 플레이어에게 공개되지 않습니다.
        </p>
        <div className="fishv2-confirm-list">
          {state.players.map((player) => {
            const done = state.currentRound.allocatedPlayerIds.includes(
              player.playerId,
            );
            return (
              <div
                key={player.playerId}
                className={`fishv2-confirm-row ${done ? "ready" : ""}`}
              >
                <span>
                  {player.playerId === myPlayerId
                    ? "나"
                    : getPlayerName(player.playerId)}
                </span>
                <strong>{done ? "완료" : "대기"}</strong>
              </div>
            );
          })}
        </div>
        <PrivateInfoBlock player={me ?? null} />
      </aside>
    </div>
  );
}

function AllocationObserverPanel({
  state,
  getPlayerName,
}: {
  readonly state: FishShopV2ViewerState;
  readonly getPlayerName: (playerId: string) => string;
}) {
  return (
    <div className="fishv2-workspace">
      <section className="fishv2-card fishv2-action-focus">
        <div className="fishv2-section-head">
          <div>
            <div className="fishv2-kicker">observer view</div>
            <h2>관전 중 · 판매권 배분 대기</h2>
          </div>
          <span className="fishv2-pill">
            {state.currentRound.allocatedPlayerIds.length}/
            {state.players.length} 완료
          </span>
        </div>
        <p className="fishv2-muted">
          관전자는 개인 판매권 수량과 아이템 상태를 볼 수 없습니다.
        </p>
      </section>

      <aside className="fishv2-card fishv2-side-panel">
        <h3>배분 진행</h3>
        <div className="fishv2-confirm-list">
          {state.players.map((player) => {
            const done = state.currentRound.allocatedPlayerIds.includes(
              player.playerId,
            );
            return (
              <div
                key={player.playerId}
                className={`fishv2-confirm-row ${done ? "ready" : ""}`}
              >
                <span>{getPlayerName(player.playerId)}</span>
                <strong>{done ? "완료" : "대기"}</strong>
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}

function PricePanel({
  state,
  myPlayerId,
  player,
  priceDraft,
  onChangePrice,
  getPlayerName,
}: {
  readonly state: FishShopV2ViewerState;
  readonly myPlayerId: string | null;
  readonly player: FishShopV2ViewerPlayer | null;
  readonly priceDraft: PriceDraft;
  readonly onChangePrice: (
    productId: FishProductId,
    price: FishPriceWon,
  ) => void;
  readonly getPlayerName: (playerId: string) => string;
}) {
  const eligibleProductIds = getEligibleProductIds(state, myPlayerId);
  const waitingCount =
    state.currentRound.priceSubmitterIds.length -
    state.currentRound.priceSubmittedPlayerIds.length;

  return (
    <div className="fishv2-workspace">
      <section className="fishv2-card fishv2-action-focus">
        <div className="fishv2-section-head">
          <div>
            <div className="fishv2-kicker">dealer room</div>
            <h2>내 희망가 제출</h2>
          </div>
          <span className="fishv2-pill">{waitingCount}명 제출 대기</span>
        </div>

        <div className="fishv2-price-stack">
          {eligibleProductIds.map((productId) => (
            <div
              key={productId}
              className={`fishv2-product-lane ${getProductMeta(productId).className}`}
            >
              <div className="fishv2-product-head">
                <ProductLabel productId={productId} />
                <span className="fishv2-pill">1천~5천</span>
              </div>
              <div className="fishv2-price-grid">
                {FISH_PRICE_WON_VALUES.map((price) => (
                  <button
                    key={price}
                    type="button"
                    className={`fishv2-price ${(priceDraft[productId] ?? 1000) === price ? "active" : ""}`}
                    data-testid={`fish-shop-v2-price-${productId}-${price}`}
                    onClick={() => onChangePrice(productId, price)}
                  >
                    {price / 1000}천
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          className="fishv2-market-summary"
          data-testid="fish-shop-v2-price-market-summary"
        >
          <div className="fishv2-kicker">public market summary</div>
          {eligibleProductIds.map((productId) => (
            <div key={productId} className="fishv2-summary-row">
              <ProductLabel productId={productId} compact />
              <span>{getMarketProductSummary(state, productId)}</span>
            </div>
          ))}
          <p className="fishv2-muted">
            판매자와 판매권 수량은 공개 정보입니다. 희망가는 결과 공개 전까지
            비공개입니다.
          </p>
        </div>
      </section>

      <section className="fishv2-card fishv2-side-panel">
        <h3>제출 상태</h3>
        <div className="fishv2-confirm-list">
          {state.currentRound.priceSubmitterIds.map((playerId) => {
            const submitted =
              state.currentRound.priceSubmittedPlayerIds.includes(playerId);
            return (
              <div
                key={playerId}
                className={`fishv2-confirm-row ${submitted ? "ready" : ""}`}
              >
                <span>
                  {playerId === myPlayerId ? "나" : getPlayerName(playerId)}
                </span>
                <strong>{submitted ? "제출됨" : "대기"}</strong>
              </div>
            );
          })}
        </div>
        <p className="fishv2-muted">
          가격 값은 결과 공개 전까지 현재 플레이어에게만 보입니다.
        </p>
        <PrivateInfoBlock player={player} />
      </section>
    </div>
  );
}

function RevealScreen({
  state,
  myPlayerId,
  player,
  getPlayerName,
  isObserver,
}: {
  readonly state: FishShopV2ViewerState;
  readonly myPlayerId: string | null;
  readonly player: FishShopV2ViewerPlayer | null;
  readonly getPlayerName: (playerId: string) => string;
  readonly isObserver: boolean;
}) {
  const confirmed = myPlayerId
    ? state.currentRound.confirmedPlayerIds.includes(myPlayerId)
    : false;

  return (
    <div className="fishv2-workspace">
      <section className="fishv2-card" data-testid="fish-shop-v2-result-table">
        <div className="fishv2-section-head">
          <div>
            <div className="fishv2-kicker">round reveal</div>
            <h2>{state.currentRound.roundNumber}라운드 결과</h2>
          </div>
          <span className="fishv2-pill">
            {state.players.length -
              state.currentRound.confirmedPlayerIds.length}
            명 확인 필요
          </span>
        </div>

        {state.currentRound.productResults.length === 0 ? (
          <div className="fishv2-status-banner">
            <strong>판매된 상품 없음</strong>
            <span className="fishv2-muted">
              이번 라운드 수입은 모든 플레이어 0원입니다.
            </span>
          </div>
        ) : (
          <div className="fishv2-reveal-stack">
            {state.currentRound.productResults.map((result) => (
              <div
                key={result.productId}
                className={`fishv2-result-card ${getProductMeta(result.productId).className}`}
              >
                <ProductLabel productId={result.productId} />
                {result.hidden ? (
                  <span className="fishv2-hidden-result">
                    비밀 처리됨 · 가격/수입 숨김
                  </span>
                ) : (
                  <>
                    <span>최저가 {formatWon(result.finalPriceWon ?? 0)}</span>
                    <span>총수입 {formatWon(result.totalRevenueWon ?? 0)}</span>
                    <strong>
                      {result.winnerIds?.map(getPlayerName).join(", ")}
                    </strong>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <aside className="fishv2-card fishv2-side-panel">
        <h3>
          {isObserver
            ? "관전 중 · 확인 대기"
            : confirmed
              ? "내 확인 완료"
              : "내 확인 필요"}
        </h3>
        <div className="fishv2-confirm-list">
          {state.players.map((player) => {
            const isConfirmed = state.currentRound.confirmedPlayerIds.includes(
              player.playerId,
            );
            return (
              <div
                key={player.playerId}
                className={`fishv2-confirm-row ${isConfirmed ? "ready" : ""}`}
              >
                <span>
                  {player.playerId === myPlayerId
                    ? "나"
                    : getPlayerName(player.playerId)}
                </span>
                <strong>{isConfirmed ? "확인 완료" : "확인 필요"}</strong>
              </div>
            );
          })}
        </div>
        <PrivateInfoBlock player={player} />
      </aside>
    </div>
  );
}

function FinalScreen({
  state,
  getPlayerName,
}: {
  readonly state: FishShopV2ViewerState;
  readonly getPlayerName: (playerId: string) => string;
}) {
  const rankedPlayers = [...state.players].sort(
    (a, b) => (b.totalIncomeWon ?? 0) - (a.totalIncomeWon ?? 0),
  );

  return (
    <section className="fishv2-card fishv2-terminal-card">
      <div className="fishv2-kicker">final income reveal</div>
      <h2>최종 수입 공개</h2>
      <div className="fishv2-final-grid">
        {rankedPlayers.map((player, index) => {
          const winner = state.winnerIds.includes(player.playerId);
          const lowest = state.lowestPlayerIds.includes(player.playerId);
          return (
            <div
              key={player.playerId}
              className={`fishv2-rank-card ${winner ? "winner" : ""} ${lowest ? "lowest" : ""}`}
            >
              <span>{index + 1}위</span>
              <strong>{getPlayerName(player.playerId)}</strong>
              <span>{formatWon(player.totalIncomeWon ?? 0)}</span>
              <span>
                가넷 {state.garnetAwardsByPlayerId[player.playerId] ?? 0}개
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SecretSheet({
  state,
  onCancel,
  onConfirm,
}: {
  readonly state: FishShopV2ViewerState;
  readonly onCancel: () => void;
  readonly onConfirm: (productId: FishProductId) => void;
}) {
  const [selectedProductId, setSelectedProductId] =
    useState<FishProductId>("HAIRTAIL");

  return (
    <div
      className="fishv2-sheet-backdrop"
      data-testid="fish-shop-v2-secret-sheet"
    >
      <div className="fishv2-sheet">
        <div className="fishv2-section-head">
          <div>
            <div className="fishv2-kicker">secret item</div>
            <h3>비밀 대상 선택</h3>
          </div>
          <span className="fishv2-pill">1회 사용</span>
        </div>
        <div className="fishv2-status-banner danger">
          <strong>확정 후 취소할 수 없습니다</strong>
          <span className="fishv2-muted">
            선택한 상품의 판매가, 수입, 최저가 제시자는 이번 라운드 결과에서
            숨겨집니다. 누가 사용했는지는 공개되지 않습니다.
          </span>
        </div>
        <div className="fishv2-choice-list">
          {PRODUCTS.map((product) => {
            const selected = selectedProductId === product.productId;
            return (
              <button
                key={product.productId}
                type="button"
                className={`fishv2-choice-row ${product.className} ${
                  selected ? "selected" : ""
                }`}
                onClick={() => setSelectedProductId(product.productId)}
              >
                <ProductLabel productId={product.productId} compact />
                <span className="fishv2-muted">
                  {getMarketProductSummary(state, product.productId)}
                </span>
                <span className="fishv2-pill">
                  {selected ? "선택됨" : "선택"}
                </span>
              </button>
            );
          })}
        </div>
        <div className="fishv2-bottom-row">
          <button
            type="button"
            className="fishv2-button grow"
            onClick={onCancel}
          >
            취소
          </button>
          <button
            type="button"
            className="fishv2-button danger grow"
            data-testid="fish-shop-v2-secret-confirm"
            onClick={() => onConfirm(selectedProductId)}
          >
            비밀 확정
          </button>
        </div>
      </div>
    </div>
  );
}

interface FishShopObserverViewProps {
  readonly viewerState: FishShopV2ViewerState;
  readonly connectionLabel: string;
  readonly getPlayerName: (playerId: string) => string;
  readonly onLeave: () => void;
  readonly leaveLabel?: string;
}

export function FishShopObserverView({
  viewerState,
  connectionLabel,
  getPlayerName,
  onLeave,
  leaveLabel = "나가기",
}: FishShopObserverViewProps) {
  const actionGuidance = getActionGuidance({
    state: viewerState,
    readOnlyObserver: true,
    canAllocate: false,
    canSubmitPrice: false,
    eligibleProductIds: [],
    ownPrice: null,
    needsResultConfirm: false,
    isHost: false,
    draftTotal: 0,
    remainingTickets: null,
  });

  return (
    <div
      className="fishv2-playground"
      data-testid="game-screen"
      data-game="fish-shop"
    >
      <div className="fishv2-app fishv2-live-shell">
        <header className="fishv2-app-top" data-testid="fish-shop-v2-top-bar">
          <div className="fishv2-titlebar">
            <div className="fishv2-titlebar-title">
              <strong data-testid="fish-shop-v2-round-indicator">
                생선가게 · {viewerState.currentRound.roundNumber}R
              </strong>
              <span>관전자 · {connectionLabel}</span>
            </div>
            <div className="fishv2-titlebar-actions">
              <span
                className="fishv2-pill"
                data-testid="fish-shop-v2-observer-banner"
              >
                관전 중
              </span>
              <span className="fishv2-pill">
                {viewerState.currentRound.allocatedPlayerIds.length}/
                {viewerState.players.length} 배분
              </span>
              <button type="button" className="fishv2-button" onClick={onLeave}>
                {leaveLabel}
              </button>
            </div>
          </div>

          <PhaseRail currentPhase={viewerState.currentRound.phase} />
          <NextActionBanner guidance={actionGuidance} />
          <PlayerRail
            state={viewerState}
            myPlayerId={null}
            getPlayerName={getPlayerName}
          />
        </header>

        <main className="fishv2-body">
          {viewerState.currentRound.phase === "TICKET_ALLOCATION" && (
            <AllocationObserverPanel
              state={viewerState}
              getPlayerName={getPlayerName}
            />
          )}

          {(viewerState.currentRound.phase === "MARKET_REVEALED" ||
            viewerState.currentRound.phase === "PRICE_SUBMISSION") && (
            <div className="fishv2-workspace">
              <MarketGrid
                state={viewerState}
                myPlayerId={null}
                getPlayerName={getPlayerName}
              />
              <aside className="fishv2-card fishv2-side-panel">
                <h3>관전 중</h3>
                <p className="fishv2-muted">
                  판매자와 판매권 수량만 공개됩니다. 희망가와 확인 결과는
                  공개되지 않습니다.
                </p>
              </aside>
            </div>
          )}

          {viewerState.currentRound.phase === "ROUND_REVEALED" && (
            <RevealScreen
              state={viewerState}
              myPlayerId={null}
              player={null}
              getPlayerName={getPlayerName}
              isObserver={true}
            />
          )}

          {viewerState.currentRound.phase === "RESTART_READY" && (
            <section className="fishv2-card fishv2-terminal-card">
              <div className="fishv2-kicker">all tied</div>
              <h2>전원 동점, 처음부터 다시 시작</h2>
              <p className="fishv2-muted">
                수입, 판매권, 아이템이 모두 초기화됩니다.
              </p>
            </section>
          )}

          {viewerState.currentRound.phase === "COMPLETED" && (
            <FinalScreen state={viewerState} getPlayerName={getPlayerName} />
          )}
        </main>

        <footer className="fishv2-bottom" data-testid="fish-shop-v2-bottom-bar">
          <div
            className="fishv2-bottom-row"
            data-testid="fish-shop-v2-observer-bottom"
          >
            <span className="fishv2-pill">관전 모드</span>
            <span className="fishv2-pill">공개 정보만 표시</span>
          </div>
          <div className="fishv2-bottom-note">리플레이 읽기 전용</div>
        </footer>
      </div>
    </div>
  );
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError, myPlayerId, isObserver } =
    useFishShopMultiplayerStore();
  const actions = fishShopMultiplayerActions;
  const [allocationDraft, setAllocationDraft] =
    useState<TicketDraft>(createTicketDraft);
  const [priceDraft, setPriceDraft] = useState<PriceDraft>({});
  const [secretSheetOpen, setSecretSheetOpen] = useState(false);

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const isRoomObserver = Boolean(
    user?.id &&
      (currentRoom?.observerUserIds ?? []).includes(user.id) &&
      !(currentRoom?.participantUserIds ?? []).includes(user.id),
  );
  const readOnlyObserver = isObserver || isRoomObserver;
  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);

  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: () => {},
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: room id + host bit are the stable init boundaries; room_updated may refresh participant objects.
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    const participantIds = currentRoom.participantUserIds ?? [];
    if (participantIds.length === 0) return;

    if (readOnlyObserver) {
      void actions.initializeAsObserver(roomId);
    } else if (isHost) {
      void actions.initializeAsHost(
        roomId,
        participantIds,
        currentRoom.hostUserId,
      );
    } else {
      void actions.initializeAsGuest(roomId);
    }

    return () => {
      actions.reset();
    };
  }, [roomId, currentRoom?.id, user?.id, isHost, readOnlyObserver]);

  const draftResetKey = `${viewerState?.currentRound.roundNumber ?? 0}:${viewerState?.currentRound.phase ?? "loading"}`;
  useEffect(() => {
    if (draftResetKey.length > 0) {
      setAllocationDraft(createTicketDraft());
      setPriceDraft({});
    }
  }, [draftResetKey]);

  const getPlayerName = (playerId: string): string =>
    getParticipantDisplayName(currentRoom, playerId, playerId);

  const eligibleProductIds = useMemo(
    () => (viewerState ? getEligibleProductIds(viewerState, myPlayerId) : []),
    [viewerState, myPlayerId],
  );
  const me =
    viewerState?.players.find((player) => player.playerId === myPlayerId) ??
    null;
  const canUseItem =
    !readOnlyObserver &&
    me?.itemStatus === "AVAILABLE" &&
    viewerState?.status === "IN_PROGRESS" &&
    viewerState.currentRound.phase !== "ROUND_REVEALED";
  const ownAllocation = viewerState?.currentRound.ownAllocation ?? null;
  const ownPrice = viewerState?.currentRound.ownPriceSubmission ?? null;
  const canAllocate =
    !readOnlyObserver &&
    viewerState?.currentRound.phase === "TICKET_ALLOCATION" &&
    !ownAllocation;
  const canSubmitPrice =
    !readOnlyObserver &&
    viewerState !== null &&
    (viewerState.currentRound.phase === "MARKET_REVEALED" ||
      viewerState.currentRound.phase === "PRICE_SUBMISSION") &&
    eligibleProductIds.length > 0 &&
    !ownPrice;
  const needsResultConfirm =
    !readOnlyObserver &&
    viewerState?.currentRound.phase === "ROUND_REVEALED" &&
    myPlayerId !== null &&
    !viewerState.currentRound.confirmedPlayerIds.includes(myPlayerId);
  const draftTotal = sumTickets(allocationDraft);

  function submitAllocation(ticketsByProduct: FishTicketsByProduct): void {
    void actions.submitAction("AllocateTickets", { ticketsByProduct });
  }

  function submitPrices(): void {
    void actions.submitAction("SubmitPrices", {
      prices: buildPrices(eligibleProductIds, priceDraft),
    });
  }

  function leaveGame(): void {
    actions.reset();
    lobbyActions.leaveGame();
    navigateTo("/fish-shop/lobby");
  }

  if (!currentRoom) {
    return (
      <div className="fishv2-playground" data-testid="fish-shop-v2-screen">
        <div className="fishv2-setup">
          <section className="fishv2-card fishv2-setup-card">
            <h1>생선가게</h1>
            <p>게임 방이 없습니다.</p>
            <button
              type="button"
              className="fishv2-button primary fishv2-full"
              onClick={() => navigateTo("/fish-shop/lobby")}
            >
              로비로 이동
            </button>
          </section>
        </div>
      </div>
    );
  }

  if (!viewerState) {
    return (
      <div
        className="fishv2-playground"
        data-testid="fish-shop-v2-screen"
        data-game="fish-shop"
      >
        <div className="fishv2-setup">
          <section className="fishv2-card fishv2-setup-card">
            <div className="fishv2-kicker">loading private view</div>
            <h1>생선가게</h1>
            <p>
              {connectionStatus === "connected"
                ? "방장이 게임 상태를 준비 중입니다."
                : "게임 서버에 연결 중입니다."}
            </p>
            {lastError && <div className="fishv2-toast">{lastError.error}</div>}
          </section>
        </div>
      </div>
    );
  }

  const actionGuidance = getActionGuidance({
    state: viewerState,
    readOnlyObserver,
    canAllocate,
    canSubmitPrice,
    eligibleProductIds,
    ownPrice,
    needsResultConfirm,
    isHost,
    draftTotal,
    remainingTickets: me?.remainingTickets ?? null,
  });
  const priceSubmitButtonLabel = canSubmitPrice
    ? "희망가 제출"
    : ownPrice
      ? "희망가 제출 완료"
      : eligibleProductIds.length === 0
        ? "제출할 상품 없음"
        : "희망가 제출 대기";
  const priceBottomNote = canSubmitPrice
    ? "내 희망가는 제출 전후 모두 결과 공개 전까지 비공개입니다."
    : ownPrice
      ? "희망가 제출 완료. 모든 판매자가 제출하면 결과가 공개됩니다."
      : eligibleProductIds.length === 0
        ? "이번 라운드 판매 상품이 없어 결과 공개를 기다립니다."
        : "제출 후 결과 공개까지 이 화면에서 대기";
  const itemButtonTitle = canUseItem
    ? "게임 중 한 번 사용할 수 있습니다."
    : "아이템은 게임 중 한 번만 사용할 수 있거나 현재 단계에서 사용할 수 없습니다.";

  return (
    <div
      className="fishv2-playground"
      data-testid="game-screen"
      data-game="fish-shop"
    >
      <div className="fishv2-app fishv2-live-shell">
        <header className="fishv2-app-top" data-testid="fish-shop-v2-top-bar">
          <div className="fishv2-titlebar">
            <div className="fishv2-titlebar-title">
              <strong data-testid="fish-shop-v2-round-indicator">
                생선가게 · {viewerState.currentRound.roundNumber}R
              </strong>
              <span>
                {readOnlyObserver
                  ? "관전자"
                  : `${getPlayerName(myPlayerId ?? "")} · 판매권 ${
                      me?.remainingTickets ?? "-"
                    }장`}{" "}
                · {connectionStatus}
              </span>
            </div>
            <div className="fishv2-titlebar-actions">
              {readOnlyObserver && (
                <span
                  className="fishv2-pill"
                  data-testid="fish-shop-v2-observer-banner"
                >
                  관전 중
                </span>
              )}
              <span className="fishv2-pill">
                {viewerState.currentRound.allocatedPlayerIds.length}/
                {viewerState.players.length} 배분
              </span>
              <button
                type="button"
                className="fishv2-button"
                onClick={leaveGame}
              >
                나가기
              </button>
            </div>
          </div>

          <PhaseRail currentPhase={viewerState.currentRound.phase} />
          <NextActionBanner guidance={actionGuidance} />
          <PlayerRail
            state={viewerState}
            myPlayerId={readOnlyObserver ? null : myPlayerId}
            getPlayerName={getPlayerName}
          />
        </header>

        <main className="fishv2-body">
          {lastError && <div className="fishv2-toast">{lastError.error}</div>}

          {viewerState.currentRound.phase === "TICKET_ALLOCATION" &&
            (readOnlyObserver ? (
              <AllocationObserverPanel
                state={viewerState}
                getPlayerName={getPlayerName}
              />
            ) : canAllocate ? (
              <AllocationBoard
                state={viewerState}
                myPlayerId={myPlayerId}
                draft={allocationDraft}
                onChangeDraft={setAllocationDraft}
                getPlayerName={getPlayerName}
              />
            ) : (
              <section className="fishv2-card fishv2-terminal-card">
                <div className="fishv2-kicker">waiting</div>
                <h2>내 판매권 배분 완료</h2>
                <p className="fishv2-muted">
                  다른 플레이어의 배분이 끝나면 시장이 자동 공개됩니다.
                </p>
              </section>
            ))}

          {(viewerState.currentRound.phase === "MARKET_REVEALED" ||
            viewerState.currentRound.phase === "PRICE_SUBMISSION") &&
            (canSubmitPrice ? (
              <PricePanel
                state={viewerState}
                myPlayerId={myPlayerId}
                player={me}
                priceDraft={priceDraft}
                onChangePrice={(productId, price) =>
                  setPriceDraft((draft) => ({ ...draft, [productId]: price }))
                }
                getPlayerName={getPlayerName}
              />
            ) : (
              <div className="fishv2-workspace">
                <MarketGrid
                  state={viewerState}
                  myPlayerId={readOnlyObserver ? null : myPlayerId}
                  getPlayerName={getPlayerName}
                />
                <aside className="fishv2-card fishv2-side-panel">
                  <h3>
                    {ownPrice
                      ? "내 희망가 제출 완료"
                      : eligibleProductIds.length === 0 && !readOnlyObserver
                        ? "가격 제출 없음"
                        : "대기 중"}
                  </h3>
                  <p className="fishv2-muted">
                    {eligibleProductIds.length === 0 && !readOnlyObserver
                      ? "이번 라운드에 판매한 상품이 없어 희망가 제출 없이 결과 공개를 기다립니다."
                      : "모든 판매자가 희망가를 제출하면 결과가 자동 공개됩니다."}
                  </p>
                  <PrivateInfoBlock player={readOnlyObserver ? null : me} />
                </aside>
              </div>
            ))}

          {viewerState.currentRound.phase === "ROUND_REVEALED" && (
            <RevealScreen
              state={viewerState}
              myPlayerId={readOnlyObserver ? null : myPlayerId}
              player={readOnlyObserver ? null : me}
              getPlayerName={getPlayerName}
              isObserver={readOnlyObserver}
            />
          )}

          {viewerState.currentRound.phase === "RESTART_READY" && (
            <section className="fishv2-card fishv2-terminal-card">
              <div className="fishv2-kicker">all tied</div>
              <h2>전원 동점, 처음부터 다시 시작</h2>
              <p className="fishv2-muted">
                방장이 수입, 판매권, 아이템을 모두 초기화합니다.
              </p>
              <div className="fishv2-reset-grid">
                <span>수입 0원</span>
                <span>판매권 6장 복구</span>
                <span>아이템 재사용 가능</span>
                <span>1라운드부터 재시작</span>
              </div>
            </section>
          )}

          {viewerState.currentRound.phase === "COMPLETED" && (
            <FinalScreen state={viewerState} getPlayerName={getPlayerName} />
          )}
        </main>

        <footer className="fishv2-bottom" data-testid="fish-shop-v2-bottom-bar">
          {readOnlyObserver ? (
            <>
              <div
                className="fishv2-bottom-row"
                data-testid="fish-shop-v2-observer-bottom"
              >
                <span className="fishv2-pill">관전 모드</span>
                <span className="fishv2-pill">공개 정보만 표시</span>
              </div>
              <div className="fishv2-bottom-note">
                플레이어 행동과 라운드 결과를 기다리는 중
              </div>
            </>
          ) : viewerState.currentRound.phase === "TICKET_ALLOCATION" ? (
            <>
              {canAllocate ? (
                <div className="fishv2-bottom-row">
                  <button
                    type="button"
                    className="fishv2-button grow"
                    onClick={() => submitAllocation(createTicketDraft())}
                  >
                    이번 라운드 패스
                  </button>
                  <button
                    type="button"
                    className="fishv2-button primary grow"
                    data-testid="fish-shop-v2-allocation-confirm"
                    onClick={() => submitAllocation(allocationDraft)}
                  >
                    {draftTotal}장 배분 확정
                  </button>
                </div>
              ) : (
                <div className="fishv2-bottom-row">
                  <button
                    type="button"
                    className="fishv2-button primary grow"
                    data-testid="fish-shop-v2-allocation-confirm"
                    disabled
                  >
                    배분 완료 · 시장 공개 대기
                  </button>
                </div>
              )}
              <div className="fishv2-bottom-note">
                전원 확정 시 시장 공개로 자동 전환
              </div>
            </>
          ) : null}

          {!readOnlyObserver &&
            (viewerState.currentRound.phase === "MARKET_REVEALED" ||
              viewerState.currentRound.phase === "PRICE_SUBMISSION") && (
              <>
                <div className="fishv2-bottom-row">
                  <button
                    type="button"
                    className="fishv2-button grow"
                    data-testid="fish-shop-v2-use-check"
                    onClick={() => void actions.submitAction("UseCheck")}
                    disabled={!canUseItem}
                    title={itemButtonTitle}
                  >
                    확인
                  </button>
                  <button
                    type="button"
                    className="fishv2-button danger grow"
                    data-testid="fish-shop-v2-open-secret"
                    onClick={() => setSecretSheetOpen(true)}
                    disabled={!canUseItem}
                    title={itemButtonTitle}
                  >
                    비밀
                  </button>
                  <button
                    type="button"
                    className="fishv2-button primary grow"
                    data-testid="fish-shop-v2-submit-prices"
                    onClick={submitPrices}
                    disabled={!canSubmitPrice}
                  >
                    {priceSubmitButtonLabel}
                  </button>
                </div>
                <div className="fishv2-bottom-note">{priceBottomNote}</div>
              </>
            )}

          {!readOnlyObserver &&
            viewerState.currentRound.phase === "ROUND_REVEALED" && (
              <>
                <div className="fishv2-bottom-row">
                  <button
                    type="button"
                    className="fishv2-button primary grow"
                    data-testid="fish-shop-v2-confirm-result"
                    onClick={() =>
                      void actions.submitAction("ConfirmRoundResult")
                    }
                    disabled={!needsResultConfirm}
                  >
                    결과 확인 완료
                  </button>
                </div>
                <div className="fishv2-bottom-note">
                  전원 확인 후 다음 라운드 또는 최종 결과로 이동
                </div>
              </>
            )}

          {!readOnlyObserver &&
            viewerState.currentRound.phase === "RESTART_READY" && (
              <div>
                <div className="fishv2-bottom-row">
                  <button
                    type="button"
                    className="fishv2-button primary grow"
                    data-testid="fish-shop-v2-restart"
                    onClick={() => void actions.submitAction("RestartGame")}
                    disabled={!isHost}
                  >
                    {isHost ? "처음부터 재시작" : "방장 재시작 대기"}
                  </button>
                </div>
                <div className="fishv2-bottom-note">
                  수입, 판매권, 아이템이 모두 초기화됩니다.
                </div>
              </div>
            )}

          {!readOnlyObserver &&
            viewerState.currentRound.phase === "COMPLETED" && (
              <div className="fishv2-bottom-row">
                <button
                  type="button"
                  className="fishv2-button primary grow"
                  onClick={leaveGame}
                >
                  로비로 이동
                </button>
              </div>
            )}
        </footer>
      </div>

      {secretSheetOpen && (
        <SecretSheet
          state={viewerState}
          onCancel={() => setSecretSheetOpen(false)}
          onConfirm={(productId) => {
            setSecretSheetOpen(false);
            void actions.submitAction("UseSecret", { productId });
          }}
        />
      )}
    </div>
  );
}
